import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageSeo from '../components/PageSeo';

const sections = [
  {
    id: "company",
    title: "Company Information",
    rows: [
      { label: "Company Name", value: "ITGER CORP LTD" },
      { label: "Registered Address", value: "Kalergi,18 Egkomi,2400, Nicosia, Cyprus" },
      { label: "Represented by / Director", value: "MICHAEL TYMVIOS" },
    ],
  },
  {
    id: "registration",
    title: "Registration and Tax Information",
    rows: [
      { label: "Company Registration Number", value: "HE495998" },
      // { label: "Tax Identification Number", value: "60346740F" },
      // { label: "VAT Identification Number", value: "CY60346740F" },
    ],
  },
  {
    id: "contact",
    title: "Contact",
    rows: [
      {
        label: "Email",
        value: "support@personalfinanceacademy.net",
        isEmail: true,
      },
    ],
  },
  {
    id: "responsible",
    title: "Responsible for Website Content",
    address: [
      "ITGER CORP LTD",
      "Kalergi,18 Egkomi",
      "2400, Nicosia",
      "Cyprus",
    ],
  },
  {
    id: "disclaimer",
    title: "Disclaimer",
    paragraph:
      "The information provided on this website is for general informational purposes only. While we make every effort to keep the content accurate and up to date, we do not accept liability for any errors, omissions, or outdated information.",
    paragraph2:
      "This imprint is provided for transparency and legal disclosure purposes. For any questions regarding the website or company information, please contact us using the email address listed above.",
  },
];

export default function Imprint() {
  return (
    <div className="flex flex-col min-h-screen font-sans text-white bg-[#0F172A] relative">
      <PageSeo
        title="Company & Legal Disclosure | Personal Finance Academy"
        description="Find Personal Finance Academy's official company information, registration details, contact information, and legal disclosures for transparency and compliance."
        canonicalUrl="https://personalfinanceacademy.net/imprint"
        ogTitle="Company & Legal Disclosure | Personal Finance Academy"
        ogDescription="Find Personal Finance Academy's official company information, registration details, contact information, and legal disclosures for transparency and compliance."
        ogImage="https://personalfinanceacademy.net/about.jpg"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": "https://personalfinanceacademy.net/imprint#webpage",
          url: "https://personalfinanceacademy.net/imprint",
          name: "Imprint | Personal Finance Academy",
          description:
            "Personal Finance Academy's imprint provides legal disclosure information, company details, contact information, and other required information about the website and its operator.",
          isPartOf: {
            "@id": "https://personalfinanceacademy.net/#website",
          },
          about: {
            "@id": "https://personalfinanceacademy.net/#organization",
          },
          publisher: {
            "@id": "https://personalfinanceacademy.net/#organization",
          },
          inLanguage: "en",
        }}
      />

      {/* GLOBAL BACKGROUND GLOWS */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1612550761236-e813928f7271?auto=format&fit=crop&q=80&w=2400"
          alt="Background"
          className="w-full h-full object-cover opacity-[0.03] mix-blend-screen"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A]/80 via-[#0F172A]/95 to-[#0F172A]" />
        <div className="absolute top-0 right-1/4 w-[600px] h-[600px] bg-[#2563EB]/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 left-1/4 w-[500px] h-[500px] bg-[#F43F5E]/10 rounded-full blur-[150px]" />
      </div>

      {/* HEADER */}
      <div className="relative z-50">
        <Header />
      </div>

      {/* MAIN CONTENT */}
      <main className="flex-1 w-full max-w-5xl mx-auto px-4 relative z-10 pt-40 pb-16 md:pb-24 md:pt-40">

        {/* Glassmorphism Document Card */}
        <div className="w-full bg-[#1E293B]/80 backdrop-blur-2xl rounded-[2.5rem] border border-slate-700/80 shadow-[0_0_50px_rgba(0,0,0,0.3)] overflow-hidden relative">

          {/* Top gradient highlight bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2563EB] via-purple-500 to-[#F43F5E] opacity-80" />

          {/* Document Header */}
          <div className="px-8 pt-16 pb-8 md:px-16 md:pt-20 md:pb-12 border-b border-slate-700/80">
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white mb-4">
              Im<span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] to-[#F43F5E]">print</span>
            </h1>
            <p className="text-slate-400 leading-relaxed">
              This page provides the legal disclosure information for this website.
            </p>
          </div>

          {/* Document Body */}
          <div className="px-8 py-12 md:px-16 md:py-16 space-y-14">

            {sections.map((section) => (
              <section key={section.id} className="space-y-6 scroll-mt-24" id={`section-${section.id}`}>

                {/* Section Heading */}
                <h2 className="text-2xl font-bold text-white tracking-tight">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] to-[#F43F5E]">
                    {section.title}
                  </span>
                </h2>

                {/* Key-Value Rows */}
                {section.rows && (
                  <div className="bg-[#0F172A]/50 border border-slate-700/80 rounded-2xl overflow-hidden divide-y divide-slate-700/60">
                    {section.rows.map((row, i) => (
                      <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 px-6 py-4 hover:bg-white/5 transition-colors">
                        <span className="text-slate-400 text-sm font-semibold min-w-[220px]">{row.label}</span>
                        {row.isEmail ? (
                          <a
                            href={`mailto:${row.value}`}
                            className="text-[#2563EB] font-bold hover:text-[#F43F5E] transition-colors hover:underline underline-offset-4"
                          >
                            {row.value}
                          </a>
                        ) : (
                          <span className="text-white font-medium">{row.value}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Address block */}
                {section.address && (
                  <div className="bg-[#0F172A]/50 border border-slate-700/80 rounded-2xl px-6 py-5 space-y-1">
                    {section.address.map((line, i) => (
                      <p key={i} className="text-slate-300 leading-relaxed">{line}</p>
                    ))}
                  </div>
                )}

                {/* Disclaimer paragraphs */}
                {section.paragraph && (
                  <div className="space-y-4">
                    <p className="text-slate-300 leading-relaxed text-lg">{section.paragraph}</p>
                    {section.paragraph2 && (
                      <p className="text-slate-300 leading-relaxed text-lg">{section.paragraph2}</p>
                    )}
                  </div>
                )}

              </section>
            ))}

          </div>
        </div>
      </main>

      {/* FOOTER */}
      <div className="relative mt-auto">
        <Footer />
      </div>
    </div>
  );
}
